<template>
  <div>
    <Dim :dimmed="favouritesOpen" @click="closeFavourites" />

    <transition
      enter-active-class="delay-200"
      enter-class="translate-x-24 opacity-0"
      enter-to-class="translate-x-0 opacity-100"
      leave-class="translate-x-0 opacity-100"
      leave-to-class="translate-x-24 opacity-0"
    >
      <aside
        v-show="favouritesOpen"
        class="bg-white overflow-x-hidden fixed inset-y-0 right-0 overflow-y-auto z-10000 p-4 pt-24 sm:px-8 ease-in-out transition-all transform duration-300 w-full xs:w-3/4 md-lg:w-2/3 lg:w-1/2"
        :class="hasFavourites ? 'max-w-3xl' : 'xl:w-2/5 3xl:w-1/3 max-w-xl'"
      >
        <section>
          <h2 class="text-center">Your Favorites</h2>
          <div v-if="hasFavourites" class="grid gap-4 xl:grid-cols-2">
            <LogoGrid
              v-for="(logo, index) in favourites.logos"
              :key="logo.id"
              :logo="logo"
              :results-index="index"
              @click="handleClick"
            />
          </div>

          <p
            v-show="!hasFavourites"
            class="mt-4 max-w-xs mx-auto text-lg text-center"
          >
            You don't have any favorites yet. Tap the

            <HeartIcon class="inline-block w-6 h-6 -mt-1 text-red-400" />

            <span class="sr-only"> Heart </span>

            icon at the top right of a logo to save it for later.
          </p>
        </section>
        <CloseButton
          v-if="favouritesOpen"
          class="right-6 top-8"
          @click.native="closeFavourites"
        />
      </aside>
    </transition>
  </div>
</template>

<script>
import LogoGrid from '@/components/results/LogoGrid'
import HeartIcon from '@/assets/svg/icons/heart.svg?inline'
import Container from '@/components/cards/Container'
import CloseButton from '@/components/CloseButton'
import Dim from '@/components/Dim'
import { EventBus, Events } from '@/lib/events'
import { mapActions, mapState } from 'vuex'

export default {
  components: {
    LogoGrid,
    CloseButton,
    Container,
    HeartIcon,
    Dim,
  },

  props: {
    favouritesOpen: { type: Boolean, default: false },
  },

  computed: {
    hasFavourites() {
      return this.favourites.logos && this.favourites.logos.length > 0
    },

    ...mapState(['favourites']),
  },

  watch: {
    favouritesOpen(newState) {
      if (newState === true) {
        document.querySelector('html').classList.add('overflow-hidden')
      } else {
        document.querySelector('html').classList.remove('overflow-hidden')
      }
    },
  },

  async mounted() {
    try {
      await this.fetchFavouriteLogos()
      this.hasFetched = true
    } catch (e) {
      // This can happen if the user isn't logged in.
      // In that case, there's nothing to fetch right away.
    }
  },

  beforeDestroy() {
    document.querySelector('html').classList.remove('overflow-hidden')
  },

  methods: {
    openFavourites(e) {
      EventBus.$emit(Events.FavoritesMenuOpened)
      this.$emit('onOpen')
    },

    closeFavourites() {
      this.$emit('onClose')
    },

    async handleClick(logo) {
      // If on editor page already, select the logo
      // Else link to editor
      if (this.$route.path.includes('/editor')) {
        this.selectLogo(logo)
      } else {
        const { wordmark, slogan } = logo.assets.template.text
        this.$store.commit('session/setWordmark', wordmark)
        if (slogan) {
          this.$store.commit('session/setSlogan', slogan)
        }

        const asset_data = {
          template: {
            id: logo.template,
          },
          assets: logo.assets,
        }

        await this.$store.dispatch('editor/addClientLogoToState', {
          logo,
          asset_data,
          params: {
            disable_history: true,
          },
        })

        this.$router.push(`/editor/colors`)
      }
    },

    selectLogo: logo => {
      EventBus.$emit(Events.EditorSelectLogo, logo)
    },
    ...mapActions('favourites', ['fetchFavouriteLogos']),
  },
}
</script>
